import { FC, useState, useEffect, useMemo, useRef } from "react";
import { Modal } from "../components/Modal";
import {
  Input,
  Select,
  Button,
  CopyableInput,
  Switch,
} from "../components/Form";
import { SortableHeader } from "../components/SortableHeader";
import {
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useUpdateEnabledUserMutation,
  useGetOrganizationsSelectQuery,
  useGetRolesSelectQuery,
  UserCreateRequest,
  UserUpdateRequest,
  UserEnabledUpdateRequest,
  UserListItemResponse,
  UserListQueryParams,
} from "../api/api";

const UserForm: FC<{
  user?: UserListItemResponse;
  onSubmit: (data: UserCreateRequest | UserUpdateRequest) => void;
  onCancel: () => void;
}> = ({ user, onSubmit, onCancel }) => {
  const { data: organizations } = useGetOrganizationsSelectQuery();
  const { data: roles } = useGetRolesSelectQuery();

  // Find initial organization and role based on user data
  const initialOrg = organizations?.find(
    (o) => o.name === user?.organization_name,
  );

  const [formData, setFormData] = useState<
    UserCreateRequest | UserUpdateRequest
  >({
    email: user?.email || "",
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    role_name: user?.role_name || "",
    organization_id: initialOrg?._id || "",
    organization_name: user?.organization_name || "",
    enabled: user?.enabled ?? true,
  });

  // Update form data when organizations or roles data is loaded
  useEffect(() => {
    if (user && organizations && !formData.organization_id) {
      const org = organizations.find((o) => o.name === user.organization_name);
      if (org) {
        setFormData((prev) => ({
          ...prev,
          organization_id: org._id,
          organization_name: org.name,
        }));
      }
    }
  }, [user, organizations, formData.organization_id]);

  useEffect(() => {
    if (user && roles && !formData.role_name) {
      const role = roles.find((r) => r.name === user.role_name);
      if (role) {
        setFormData((prev) => ({
          ...prev,
          role_name: role.name,
        }));
      }
    }
  }, [user, roles, formData.role_name]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {user && <CopyableInput label="ID" value={user.id} />}
      <Input
        label="Email"
        type="email"
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        required
      />
      <Input
        label="First Name"
        value={formData.first_name}
        onChange={(e) =>
          setFormData({ ...formData, first_name: e.target.value })
        }
        required
      />
      <Input
        label="Last Name"
        value={formData.last_name}
        onChange={(e) =>
          setFormData({ ...formData, last_name: e.target.value })
        }
        required
      />
      <Select
        label="Organization"
        value={formData.organization_id}
        onChange={(e) => {
          const org = organizations?.find((o) => o._id === e.target.value);
          setFormData({
            ...formData,
            organization_id: e.target.value,
            organization_name: org?.name || "",
          });
        }}
        options={organizations || []}
        required
      />
      <Select
        label="Role"
        value={formData.role_name}
        onChange={(e) => {
          const role = roles?.find((r) => r.name === e.target.value);
          setFormData({
            ...formData,
            role_name: role?.name || "",
          });
        }}
        options={roles || []}
        bindValue={"name"}
        required
      />
      {user && (
        <Switch
          label="Enabled"
          checked={!!(formData as UserUpdateRequest).enabled}
          onChange={(e) => {
            setFormData({
              ...formData,
              enabled: e.target.checked,
            });
          }}
        />
      )}
      <div className="flex justify-end space-x-2">
        <Button type="button" onClick={onCancel} className="bg-gray-600">
          Cancel
        </Button>
        <Button type="submit">{user ? "Update" : "Create"}</Button>
      </div>
    </form>
  );
};

export const Users: FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<
    UserListItemResponse | undefined
  >();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState<string>("All Roles");
  const [selectedOrg, setSelectedOrg] = useState<string>("");
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc" | null;
  } | null>(null);
  const [uploadError, setUploadError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const PAGE_SIZE = 20;

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  const requestSort = (key: string) => {
    const nextDirection =
      sortConfig?.key === key && sortConfig.direction === "asc"
        ? "desc"
        : "asc";

    setSortConfig({ key, direction: nextDirection });
    setPage(1);
  };

  const queryParams = useMemo<UserListQueryParams>(() => {
    const params: UserListQueryParams = { page, limit: PAGE_SIZE };
    const trimmedSearch = debouncedSearchTerm;

    if (trimmedSearch) {
      params.search = trimmedSearch;
    }
    if (selectedRole !== "All Roles") {
      params.roles = [selectedRole];
    }
    if (selectedOrg) {
      params.organization_id = selectedOrg;
    }
    if (sortConfig?.direction) {
      params.column_key = sortConfig.key;
      params.order = sortConfig.direction.toUpperCase();
    }

    return params;
  }, [page, debouncedSearchTerm, selectedOrg, selectedRole, sortConfig]);

  const {
    data: users_response,
    isLoading,
    isFetching,
  } = useGetUsersQuery(queryParams);
  const { data: organizations } = useGetOrganizationsSelectQuery();
  const { data: roles } = useGetRolesSelectQuery();
  const [createUser] = useCreateUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [updateEnabledUser] = useUpdateEnabledUserMutation();
  const users = users_response?.users ?? [];

  const uniqueRoles = useMemo(() => {
    const roleNames = roles?.map((role) => role.name) ?? [];
    return ["All Roles", ...roleNames];
  }, [roles]);

  const uniqueOrgs = useMemo(() => {
    const organizationOptions = organizations ?? [];
    return [{ _id: "", name: "All Organizations" }, ...organizationOptions];
  }, [organizations]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, selectedRole, selectedOrg]);

  const totalUsers = users_response?.total ?? 0;
  const currentPage = users_response?.page ?? page;
  const totalPages = Math.max(
    1,
    Math.ceil(totalUsers / (users_response?.limit ?? PAGE_SIZE)),
  );
  const fromItem =
    totalUsers === 0
      ? 0
      : (currentPage - 1) * (users_response?.limit ?? PAGE_SIZE) + 1;
  const toItem =
    totalUsers === 0
      ? 0
      : Math.min(
          currentPage * (users_response?.limit ?? PAGE_SIZE),
          totalUsers,
        );

  useEffect(() => {
    setPage((currentPageValue) => Math.min(currentPageValue, totalPages));
  }, [totalPages]);

  const handleSubmit = async (data: UserCreateRequest | UserUpdateRequest) => {
    try {
      if (selectedUser) {
        const update_data = data as UserUpdateRequest;
        await updateUser({ id: selectedUser.id, body: update_data });
        await updateEnabledUser({
          id: selectedUser.id,
          body: { enabled: update_data.enabled } as UserEnabledUpdateRequest,
        });
      } else {
        await createUser(data as UserCreateRequest);
      }
      setIsModalOpen(false);
      setSelectedUser(undefined);
    } catch (error) {
      console.error("Error saving user:", error);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError("");
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split("\n");
        const headers = lines[0].split(",").map((h) => h.trim());

        // Validate headers
        const requiredHeaders = [
          "first_name",
          "last_name",
          "email",
          "organization_name",
          "role_name",
        ];
        const missingHeaders = requiredHeaders.filter(
          (h) => !headers.includes(h),
        );
        if (missingHeaders.length > 0) {
          throw new Error(
            `Missing required headers: ${missingHeaders.join(", ")}`,
          );
        }

        // Get header indices
        const headerIndices = {
          first_name: headers.indexOf("first_name"),
          last_name: headers.indexOf("last_name"),
          email: headers.indexOf("email"),
          organization_name: headers.indexOf("organization_name"),
          role_name: headers.indexOf("role_name"),
        };

        // Process each line
        const users: UserCreateRequest[] = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          const values = line.split(",").map((v) => v.trim());
          const organization = organizations?.find(
            (o) => o.name === values[headerIndices.organization_name],
          );
          const role = roles?.find(
            (r) => r.name === values[headerIndices.role_name],
          );

          if (!organization) {
            throw new Error(
              `Organization not found: ${values[headerIndices.organization_name]}`,
            );
          }
          if (!role) {
            throw new Error(
              `Role not found: ${values[headerIndices.role_name]}`,
            );
          }

          users.push({
            first_name: values[headerIndices.first_name],
            last_name: values[headerIndices.last_name],
            email: values[headerIndices.email],
            organization_id: organization._id,
            organization_name: organization.name,
            role_id: role._id,
            role_name: role.name,
          });
        }

        // Create users
        for (const user of users) {
          await createUser(user);
        }

        setIsUploadModalOpen(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } catch (error) {
        setUploadError(
          error instanceof Error ? error.message : "Failed to process CSV file",
        );
      }
    };

    reader.readAsText(file);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Users</h1>
        <div className="space-x-2">
          <Button onClick={() => setIsUploadModalOpen(true)}>Upload CSV</Button>
          <Button onClick={() => setIsModalOpen(true)}>Create User</Button>
        </div>
      </div>

      <div className="mb-6 space-y-4">
        <div className="flex space-x-4">
          <div className="flex-1">
            <Input
              label="Search"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="w-64">
            <Select
              label="Role"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              options={uniqueRoles.map((role) => ({ _id: role, name: role }))}
              showEmptyOption={false}
            />
          </div>
          <div className="w-64">
            <Select
              label="Organization"
              value={selectedOrg}
              onChange={(e) => setSelectedOrg(e.target.value)}
              options={uniqueOrgs}
              showEmptyOption={false}
            />
          </div>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader<UserListItemResponse>
                label="Name"
                sortKey="first_name"
                currentSort={
                  sortConfig as {
                    key: keyof UserListItemResponse;
                    direction: "asc" | "desc" | null;
                  } | null
                }
                onSort={requestSort}
              />
              <SortableHeader<UserListItemResponse>
                label="Email"
                sortKey="email"
                currentSort={
                  sortConfig as {
                    key: keyof UserListItemResponse;
                    direction: "asc" | "desc" | null;
                  } | null
                }
                onSort={requestSort}
              />
              <SortableHeader<UserListItemResponse>
                label="Role"
                sortKey="role_name"
                currentSort={
                  sortConfig as {
                    key: keyof UserListItemResponse;
                    direction: "asc" | "desc" | null;
                  } | null
                }
                onSort={requestSort}
              />
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Organization
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users?.map((user) => (
              <tr
                key={user.id}
                onClick={() => {
                  setSelectedUser(user);
                  setIsModalOpen(true);
                }}
                className="cursor-pointer"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.first_name} {user.last_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{user.email}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.role_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.organization_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.enabled ? "enabled" : "disabled"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
          <div className="text-sm text-gray-500">
            {totalUsers === 0
              ? "Нет результатов"
              : `Показано ${fromItem}-${toItem} из ${totalUsers}`}
          </div>
          <div className="text-sm text-gray-500">
            Страница {currentPage} из {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() =>
                setPage((currentPageValue) => Math.max(1, currentPageValue - 1))
              }
              className="cursor-pointer rounded-md bg-gray-600 px-4 py-2 text-sm font-medium text-white"
              disabled={currentPage <= 1 || isFetching}
            >
              Назад
            </button>
            <button
              type="button"
              onClick={() =>
                setPage((currentPageValue) =>
                  Math.min(totalPages, currentPageValue + 1),
                )
              }
              className="cursor-pointer rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white"
              disabled={currentPage >= totalPages || isFetching}
            >
              Вперед
            </button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedUser(undefined);
        }}
        title={selectedUser ? "Edit User" : "Create User"}
      >
        <UserForm
          user={selectedUser}
          onSubmit={handleSubmit}
          onCancel={() => {
            setIsModalOpen(false);
            setSelectedUser(undefined);
          }}
        />
      </Modal>

      <Modal
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false);
          setUploadError("");
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }}
        title="Upload Users CSV"
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            <p>Please upload a CSV file with the following headers:</p>
            <code className="block mt-2 p-2 bg-gray-100 rounded">
              first_name,last_name,email,organization_name,role_name
            </code>
            <p className="mt-2">Example:</p>
            <code className="block mt-2 p-2 bg-gray-100 rounded">
              John,Doe,john.doe@example.com,Organization Name,author
            </code>
          </div>
          <Input
            label="CSV File"
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            ref={fileInputRef}
            className="cursor-pointer"
          />
          {uploadError && (
            <div className="text-sm text-red-600">{uploadError}</div>
          )}
          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              onClick={() => {
                setIsUploadModalOpen(false);
                setUploadError("");
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
              className="bg-gray-600"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
